import { useState } from 'react';
import type { Action, ContentBlock } from '@beidou/agent-protocol';
import { BlockRenderer } from '../renderer/BlockRenderer';

export type ResultSort = 'default' | 'title';

interface ResultPaneProps {
  blocks: ContentBlock[];
  onAction: (action: Action) => void;
  emptyHint?: string;
  suggestedPrompts?: string[];
  onSendMessage?: (text: string) => void;
}

function sortBlocks(blocks: ContentBlock[], sort: ResultSort): ContentBlock[] {
  if (sort === 'default') return blocks;
  return [...blocks].sort((a, b) => {
    const ta = 'title' in a && a.title ? String(a.title) : '';
    const tb = 'title' in b && b.title ? String(b.title) : '';
    return ta.localeCompare(tb, 'zh-CN');
  });
}

export function ResultPane({
  blocks,
  onAction,
  emptyHint = '推荐结果将显示在这里',
  suggestedPrompts = [],
  onSendMessage,
}: ResultPaneProps) {
  const [sort, setSort] = useState<ResultSort>('default');

  const handleCardAction = (action: Action) => {
    if (action.type === 'open_item' && action.payload && typeof action.payload === 'object') {
      const title = (action.payload as { title?: string }).title;
      const itemId = (action.payload as { item_id?: string }).item_id;
      if (onSendMessage && title) {
        onSendMessage(`介绍一下《${title}》`);
        return;
      }
      if (onSendMessage && itemId) {
        onSendMessage(`介绍一下 item ${itemId}`);
        return;
      }
    }
    onAction(action);
  };

  const sorted = sortBlocks(blocks, sort);

  return (
    <aside className="result-pane" aria-label="推荐结果">
      <div className="result-pane-header">
        <span>推荐结果</span>
        {blocks.length > 0 && (
          <select
            className="result-pane-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value as ResultSort)}
            aria-label="排序"
          >
            <option value="default">默认顺序</option>
            <option value="title">按标题</option>
          </select>
        )}
      </div>
      <div className="result-pane-body">
        {sorted.length === 0 ? (
          <div className="result-pane-empty">
            <p>{emptyHint}</p>
            {suggestedPrompts.length > 0 && (
              <div className="result-pane-suggestions">
                {suggestedPrompts.slice(0, 4).map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="suggestion-chip"
                    onClick={() => onSendMessage?.(s)}
                    disabled={!onSendMessage}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          sorted.map((block, i) => (
            <div key={i} className="result-pane-block">
              <BlockRenderer block={block} onAction={handleCardAction} variant="result" />
            </div>
          ))
        )}
      </div>
    </aside>
  );
}
